import React, {useEffect, useState, useRef} from 'react';

import {
  addToFormObject,
  multipleAddToFormObject,
  multipleUpdates,
  setupNewFormObject,
  updateFormObject,
} from '../formValidation';
import request from '../../api/request';
import {handleError, toasterSuccess} from '..';

export {default as useFlags} from './useFlags';

export const useAsyncLoad = (func, ...params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [reload, setReload] = useState(false);

  const replaceResultItem = (item, idField) =>
    setData({
      ...data,
      results: data.results.map(x => (x[idField] === item[idField] ? item : x)),
    });

  useEffect(
    () => {
      if (reload && !isLoading) {
        load();
      }
    },
    [reload, isLoading],
  );

  const load = async (overrideParams = null, keepLoading = false) => {
    let d = null;

    if (isLoading) {
      setReload(true);
      return data;
    }
    setReload(false);
    setLoading(true);
    setIsLoading(true);

    try {
      d = overrideParams ? await func(overrideParams) : await func(...params);
      setData(d);
    } catch (error) {
      throw new Error(error);
    } finally {
      if (!keepLoading) {
        setLoading(false);
        setIsLoading(false);
      }
    }

    return d;
  };

  const startLoading = () => {
    setLoading(true);
    setIsLoading(true);
  };

  const stopLoading = () => {
    setLoading(false);
    setIsLoading(false);
  };

  return {
    data,
    load,
    loading,
    startLoading,
    stopLoading,
    setData,
    replaceResultItem,
    results: data && data.results ? data.results : [],
  };
};

export const useFormFields = (formObject = {}, existingData = {}) => {
  const [fields, setFields] = useState(
    setupNewFormObject(formObject, existingData),
  );

  const replaceFormObject = (form, existing) =>
    setFields(setupNewFormObject(form, existing));

  const updateField = (value, field) =>
    setFields(prevState => updateFormObject(prevState, field, value));

  const updateFields = fields => {
    let returnObject = null;
    setFields(prevState => {
      returnObject = multipleUpdates(prevState, fields);
      return returnObject;
    });
    return returnObject;
  };

  const addField = (properties, field) =>
    setFields(prevState => addToFormObject(prevState, properties, field));

  const addFields = fields =>
    setFields(prevState => multipleAddToFormObject(prevState, fields));

  return {
    fields,
    addField,
    addFields,
    replaceFormObject,
    updateField,
    updateFields,
  };
};

export const useSelection = allItems => {
  const [selected, setSelected] = useState(new Set());

  const updateAll = checked => setSelected(new Set(checked ? allItems : []));

  const update = (item, checked) => {
    const newSelected = new Set(selected);
    if (checked) {
      newSelected.add(item);
    } else {
      newSelected.delete(item);
    }
    setSelected(newSelected);
  };

  const isSelected = item => selected.has(item);

  return {
    items: selected ? Array.from(selected) : [],
    count: selected.size,
    anySelected: selected.size > 0,
    allSelected: allItems.length > 0 && selected.size === allItems.length,
    isSelected,
    update,
    updateAll,
  };
};

export const useLocalStorage = key => ({
  value: localStorage.getItem(key),
  valueAsBool: localStorage.getItem(key) === 'true',
  remove: () => localStorage.removeItem(key),
  set: value => localStorage.setItem(key, value),
});

export const useRequest = (
  params,
  {errorMsg = null, successMessage = null, autoload = true, title = ''} = {},
) => {
  const [loading, setLoading] = useState(autoload);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (autoload) {
      load();
    }
  }, []);

  const load = async (newParams = {}) => {
    setLoading(true);
    try {
      const response = await request({...params, ...newParams});
      const result = response && response.data ? response.data : null;

      setData(result);
      setLoading(false);
      return result;
    } catch (error) {
      handleError(errorMsg || `Unable to load ${title}`, error);
    }
  };

  const post = async (postParams = {}) => {
    setLoading(true);
    try {
      const response = await request({
        method: 'POST',
        ...params,
        ...postParams,
      });

      // 50002: IgnoredException
      if (response && response.data && response.data.code === 50002) {
        return null;
      }

      if (response && response.status >= 400) {
        if (response.data) {
          handleError('An error occurred', response.data);
          return {error: true, data: response.data};
        }

        throw new Error(
          response.data && response.data.message
            ? response.data.message
            : response.data,
        );
      }

      if (successMessage) {
        toasterSuccess(successMessage);
      }

      if (response && response.data) {
        setData(response.data);
      }

      return response;
    } catch (error) {
      handleError(errorMsg || `Unable to save ${title}`, error);
      return {error: true, data: error};
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (deleteId = null) => {
    try {
      const response = await request({
        method: 'DELETE',
        ...params,
        url: `${params.url}/${deleteId}`,
      });

      if (response && response.status >= 400) {
        throw new Error(
          response.data && response.data.message
            ? response.data.message
            : response.data,
        );
      }

      if (successMessage) {
        toasterSuccess(successMessage);
      }

      return response || true;
    } catch (error) {
      handleError(errorMsg || `Unable to delete ${title}`, error);
      return false;
    }
  };

  return {
    loading,
    data,
    load,
    post,
    deleteItem,
    setData,
    setLoading,
    results: data && data.results ? data.results : [],
  };
};

export const useFocusRef = focused => {
  const ref = useRef(null);
  useEffect(
    () => {
      if (focused) {
        ref.current.focus();
      }
    },
    [focused],
  );
  return ref;
};



// WEBPACK FOOTER //
// ./src/helpers/customHooks/index.js